import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Mail,
  CheckCircle,
  AlertCircle,
  Leaf,
  ArrowLeft,
  RefreshCw,
  Shield
} from 'lucide-react';
import { useI18n } from '../hooks/useI18n';
import LanguageSwitcher from "../components/LanguageSwitcher";

const EmailVerificationPage: React.FC = () => {
  const { t, isRTL } = useI18n();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [token, setToken] = useState<string>('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError(t('auth.emailVerification.invalidLink'));
      setLoading(false);
      return;
    }
    setToken(tokenParam);
    verifyEmail(tokenParam);
  }, [searchParams]);

  const verifyEmail = async (verificationToken: string): Promise<void> => {
    try {
      const response: any = await authAPI.verifyEmail(verificationToken);

      if (response.message && response.message.includes('successfully')) {
        // Email verified successfully - clear old unverified token and redirect to login
        logout(); // Clear localStorage and invalidate old token
        setMessage(t('auth.emailVerification.success'));

        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.message || t('auth.emailVerification.failed'));
      }
    } catch (error: unknown) {
      const apiError = error as any;
      setError(apiError.response?.data?.message || t('auth.emailVerification.failedGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async (): Promise<void> => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await authAPI.resendVerification(token);
      setMessage(response.message || t('auth.emailVerification.resendSuccess'));
    } catch (error: unknown) {
      const apiError = error as any;
      setError(apiError.response?.data?.message || t('auth.emailVerification.resendFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-100 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-secondary-900">{t('auth.emailVerification.verifyingTitle')}</h2>
              <p className="text-secondary-600">{t('auth.emailVerification.verifyingMessage')}</p>
            </div>
            <div className="card-body text-center">
              <div className="w-12 h-12 spinner mx-auto mb-4"></div>
              <p className="text-secondary-600">{t('auth.emailVerification.verifyingStatus')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-primary-50 to-secondary-100 flex ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 flex items-center justify-center w-full">
          <div className="max-w-md text-center text-white px-8">
            <Link to="/" className="flex items-center justify-center mb-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mr-4">
                <img src="/solvingers.svg" />
              </div>
              <h1 className="text-4xl font-bold">Facturelik</h1>
            </Link>

            <h2 className="text-3xl font-semibold mb-4">{t('auth.emailVerification.pageTitle')}</h2>
            <p className="text-lg text-white/90 mb-12">
              {t('auth.emailVerification.pageSubtitle')}
            </p>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold mb-2">{t('auth.emailVerification.checkEmailTitle')}</h3>
                  <p className="text-white/80">{t('auth.emailVerification.checkEmailDesc')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold mb-2">{t('auth.emailVerification.secureTitle')}</h3>
                  <p className="text-white/80">{t('auth.emailVerification.secureDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Content */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden flex items-center justify-center mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-3">
              <img src="/solvingers.svg" />
            </div>
            <h1 className="text-2xl font-bold text-secondary-900">Facturelik</h1>
          </Link>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-secondary-900 mb-2">{t('auth.emailVerification.mainTitle')}</h2>
            <p className="text-secondary-600">{t('auth.emailVerification.mainSubtitle')}</p>
          </div>

          {message && (
            <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0" />
              <p className="text-success-800">{message}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
              <p className="text-error-800">{error}</p>
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <div className="space-y-4">
                <Link
                  to="/login"
                  className="btn-primary w-full flex items-center justify-center space-x-2"
                >
                  <ArrowLeft className={`h-5 w-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
                  <span>{t('auth.emailVerification.backToLogin')}</span>
                </Link>

                {error && (
                  <button
                    onClick={handleResendVerification}
                    className="btn-outline w-full flex items-center justify-center space-x-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 spinner"></div>
                        <span>{t('auth.emailVerification.sending')}</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        <span>{t('auth.emailVerification.resendButton')}</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
